import { z } from 'zod';
import { pageQuerySchema } from './http.js';
/**
 * The widest window an aggregation will answer over. A quarter is the longest span a
 * dashboard range control will offer, and a cap is the ONLY bound an aggregation has —
 * unlike a paged list, which `pageSize` bounds regardless of the date range (milestone
 * Constraint 11).
 */
export const ANALYTICS_MAX_RANGE_DAYS = 92;
/** How many hosts `topHosts` returns. A leaderboard, not a directory. */
export const ANALYTICS_TOP_HOSTS_LIMIT = 5;
/**
 * A LOCAL calendar day, `YYYY-MM-DD`, in the location's own timezone — deliberately NOT
 * an ISO 8601 instant, because it names a day somebody experienced, not a moment in UTC.
 */
export const ANALYTICS_LOCAL_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
/**
 * `GET` query for both Phase 1 aggregation endpoints — the breakdown and the timeline.
 *
 * Unlike `visitFilterQuerySchema` in ./visit.js, `from` and `to` are REQUIRED and the
 * refinement below caps the span: a paged list is bounded by `pageSize` regardless of the
 * date range, but an aggregation with no bound is an unbounded scan of every visit a
 * tenant has ever had (milestone Constraint 11). Deliberately independent of
 * `visitFilterQuerySchema` — see `visit.ts` for the sibling shape, never imported here.
 *
 * `to > from` is enforced strictly: a zero-width range is always a client bug, never an
 * empty result worth answering. When `to <= from`, only that issue is raised (`return`
 * before the span check), so the caller gets one unambiguous message instead of two.
 */
const analyticsRangeShape = {
    from: z.string().datetime({ offset: true }),
    to: z.string().datetime({ offset: true }),
    locationId: z.string().min(1).optional(),
};
/**
 * The span refinement, factored out so the paged variant below cannot drift from it.
 *
 * It is a FUNCTION and not a second schema because `analyticsRangeQuerySchema` is a
 * `ZodEffects` once `.superRefine` is applied, and a `ZodEffects` cannot be `.merge`d with
 * `pageQuerySchema` — the paged query has to be built from the raw shape and then refined
 * with the identical rule. Two hand-copied refinements would be two rules that agree until
 * somebody edits one.
 */
function refineAnalyticsRange(value, ctx) {
    const from = new Date(value.from).getTime();
    const to = new Date(value.to).getTime();
    if (to <= from) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: 'to must be after from' });
        return;
    }
    if (to - from > ANALYTICS_MAX_RANGE_DAYS * 24 * 60 * 60 * 1000) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['to'],
            message: `range must not exceed ${ANALYTICS_MAX_RANGE_DAYS} days`,
        });
    }
}
export const analyticsRangeQuerySchema = z.object(analyticsRangeShape).superRefine(refineAnalyticsRange);
/**
 * The same range, plus `page`/`pageSize`/`sort` — for the ONE report that is a ranked list
 * rather than a fixed-size aggregate (hosts).
 *
 * Both bounds apply, and neither is redundant: `pageSize` bounds the rows returned, and the
 * span cap still bounds the SCAN those rows are ranked out of. Dropping the cap because the
 * response is paged is exactly the mistake milestone Constraint 11 exists to prevent.
 */
export const analyticsPagedRangeQuerySchema = z
    .object({ ...analyticsRangeShape, ...pageQuerySchema.shape })
    .superRefine(refineAnalyticsRange);
/** One location's visit count within the requested range. An allow-list (S7): id, name, count. */
export const visitLocationCountDTOSchema = z.object({
    locationId: z.string(),
    locationName: z.string(),
    count: z.number().int().nonnegative(),
});
/** One host's visit count within the requested range. An allow-list (S7): id, name, count. */
export const visitHostCountDTOSchema = z.object({
    hostPersonId: z.string(),
    hostName: z.string(),
    count: z.number().int().nonnegative(),
});
/**
 * One visitor type's visit count within the requested range.
 *
 * BOTH ids ARE NULLABLE TOGETHER, and that is the whole point: `VisitorVisit.visitorTypeId`
 * is optional (schema.prisma), so visits taken through a flow with no type selection —
 * every visit made before S4's visitor-type catalogue existed, and every walk-up on a flow
 * without a selection screen — belong to a real bucket that has no row to name it. Reporting
 * that bucket as zero would quietly lose visits from a total the same response also states;
 * the client labels it instead (`admin.reports.traffic.untypedVisitor`). There is at most one
 * such row per response.
 */
export const visitVisitorTypeCountDTOSchema = z.object({
    visitorTypeId: z.string().nullable(),
    visitorTypeName: z.string().nullable(),
    count: z.number().int().nonnegative(),
});
/**
 * `GET /api/web/analytics/breakdown` response — totals, by location, by visitor type, and the
 * top hosts leaderboard (capped server-side at `ANALYTICS_TOP_HOSTS_LIMIT`). No email, no
 * phone, no visitor id, no visit ids (S7).
 */
export const visitBreakdownDTOSchema = z.object({
    totalVisits: z.number().int().nonnegative(),
    byLocation: z.array(visitLocationCountDTOSchema),
    /** Ranked descending, ties broken by name; the untyped bucket (if any) sorts by count like
     *  every other row rather than being pinned — it is a real category, not a footnote. */
    byVisitorType: z.array(visitVisitorTypeCountDTOSchema),
    topHosts: z.array(visitHostCountDTOSchema),
});
/** One local calendar day's visit count. `date` is `ANALYTICS_LOCAL_DAY_PATTERN`, never an instant. */
export const visitDayCountDTOSchema = z.object({
    date: z.string().regex(ANALYTICS_LOCAL_DAY_PATTERN),
    count: z.number().int().nonnegative(),
});
/** One hour-of-day bucket's visit count, in the location's local time. */
export const visitHourCountDTOSchema = z.object({
    hour: z.number().int().min(0).max(23),
    count: z.number().int().nonnegative(),
});
/** `GET /api/web/analytics/timeline` response. */
export const visitTimelineDTOSchema = z.object({
    byDay: z.array(visitDayCountDTOSchema),
    /** ALWAYS 24 entries, hour 0…23, zeros included — a histogram with holes is unreadable. */
    byHourOfDay: z.array(visitHourCountDTOSchema).length(24),
});
/**
 * `GET /api/web/analytics/summary` response — the Reports Overview's KPI row (S25).
 *
 * EVERY NUMBER HERE IS SCOPED TO THE REQUESTED RANGE, without exception. The temptation is to
 * mix in a live "currently on site" count, because that is the number a front desk wants; it is
 * also the number that makes a report irreproducible — the same range answers differently every
 * minute and nobody can tell which of the tiles moved because the range moved. The live roster
 * has its own surface (`GET /evacuation/roster`, S14) and this one links to it.
 *
 * Deliberately disjoint from `visitBreakdownDTOSchema` rather than merged into it: the dashboard
 * (S16) calls `breakdown` on every load and must not start paying for the two extra aggregations
 * below to render four tiles it does not show.
 */
export const visitSummaryDTOSchema = z.object({
    totalVisits: z.number().int().nonnegative(),
    /** Distinct `visitorId` in range — a visitor who came four times counts once. */
    uniqueVisitors: z.number().int().nonnegative(),
    /** Distinct `visitorId` with MORE THAN ONE visit in range. Always <= `uniqueVisitors`. */
    returningVisitors: z.number().int().nonnegative(),
    /** Visits that redeemed an invite. `totalVisits - preRegisteredVisits` is the walk-up count,
     *  so it is not also stated — two fields that must agree are one field and a bug waiting. */
    preRegisteredVisits: z.number().int().nonnegative(),
    /** Visits still `CHECKED_IN` — for a range that ended, this is the check-out compliance gap,
     *  not a live occupancy number. */
    neverCheckedOutVisits: z.number().int().nonnegative(),
    /** Mean minutes between check-in and check-out, over CHECKED-OUT visits only; `null` when
     *  none of the visits in range were ever checked out. Rounded to one decimal by the producer:
     *  a mean dwell of "47.3 minutes" is a number, "47.28571428571429" is a leak of the divisor. */
    averageVisitMinutes: z.number().nonnegative().nullable(),
    /** `totalVisits` divided by the number of whole days the range spans, one decimal. Always a
     *  number: the range schema refuses a zero-width span, so the divisor is never 0. */
    averageVisitsPerDay: z.number().nonnegative(),
});
/* -------------------------------------------------------------------------- */
/* S25 Phase 2 — the four PROFESSIONAL reports (`visitor.reports_advanced`)    */
/* -------------------------------------------------------------------------- */
/**
 * One row of the Hosts report — the paged leaderboard, not the dashboard's top-5.
 *
 * `uniqueVisitors` is deliberately beside `visits`: a host with 40 visits from 3 people is
 * running a standing meeting, and a host with 40 visits from 40 people is running a lobby.
 * Those are different facilities problems and one number cannot tell them apart.
 *
 * An allow-list (S7): no email, no phone, no department. A leaderboard is not a directory.
 */
export const hostReportRowDTOSchema = z.object({
    hostPersonId: z.string(),
    hostName: z.string(),
    visits: z.number().int().nonnegative(),
    uniqueVisitors: z.number().int().nonnegative(),
    /** The most recent check-in this host received in range. An instant, never a local day —
     *  this one is a moment, unlike `visitDayCountDTO.date`. */
    lastVisitAt: z.string().datetime(),
});
/**
 * One dwell-time bucket. `upToMinutes: null` is the OPEN-ENDED top bucket ("4 hours and
 * over") — never a magic number, the same nullable-means-unbounded convention
 * `Entitlement.limit` uses.
 */
export const visitDurationBucketDTOSchema = z.object({
    upToMinutes: z.number().int().positive().nullable(),
    count: z.number().int().nonnegative(),
});
/**
 * Peak simultaneous occupancy for one local hour of day, across the whole range.
 *
 * "Simultaneous" is the point and is NOT a check-in count: a visit occupies every hour
 * between its check-in and its check-out, so the 9 a.m. bucket counts everyone who was in
 * the building at 9, not everyone who arrived at 9. `peak` is the MAXIMUM such count over
 * the range's days, because an occupancy number a facilities manager can act on is the
 * worst case, not the average — an average of 12 hides the morning of 60.
 */
export const occupancyHourDTOSchema = z.object({
    hour: z.number().int().min(0).max(23),
    peak: z.number().int().nonnegative(),
});
/**
 * `GET /api/web/analytics/occupancy` response — how long visitors stay and how many are in
 * at once. Professional tier (`visitor.reports_advanced`).
 *
 * Every percentile is `null` together with `averageVisitMinutes` when nothing in range was
 * ever checked out: a median of 0 would be a claim, and there is nothing to claim.
 */
export const visitOccupancyDTOSchema = z.object({
    checkedOutVisits: z.number().int().nonnegative(),
    /** The check-out compliance gap — visits still `CHECKED_IN` after the range ended. These
     *  are EXCLUDED from every duration statistic below: a visit nobody closed has no
     *  duration, and imputing one would quietly inflate the mean for every tenant whose front
     *  desk forgets. */
    neverCheckedOutVisits: z.number().int().nonnegative(),
    averageVisitMinutes: z.number().nonnegative().nullable(),
    medianVisitMinutes: z.number().nonnegative().nullable(),
    p90VisitMinutes: z.number().nonnegative().nullable(),
    /** Ascending by `upToMinutes`, open-ended bucket last. Always the same bucket edges, so
     *  two ranges are comparable. */
    durationBuckets: z.array(visitDurationBucketDTOSchema),
    /** ALWAYS 24 entries, hour 0…23, zeros included — same reason `byHourOfDay` is. */
    peakOnSiteByHourOfDay: z.array(occupancyHourDTOSchema).length(24),
});
/**
 * `GET /api/web/analytics/invites` response — does pre-registration actually convert?
 * Professional tier (`visitor.reports_advanced`).
 *
 * INVITE COUNTS ARE KEYED ON `createdAt`, VISIT COUNTS ON `checkedInAt`. They are two
 * different populations over the same window and the response states both rather than
 * pretending one explains the other — an invite raised on the last day of the range is
 * counted here and its arrival, next week, is not.
 */
export const inviteFunnelDTOSchema = z.object({
    invitesCreated: z.number().int().nonnegative(),
    redeemed: z.number().int().nonnegative(),
    cancelled: z.number().int().nonnegative(),
    /** Still `PENDING` with `expiresAt` in the past. There is no `EXPIRED` status in the
     *  enum — expiry is a fact about the clock, not a stored transition, so it is derived
     *  here rather than invented in the schema. */
    expired: z.number().int().nonnegative(),
    /** Still `PENDING` and not yet expired — genuinely outstanding. */
    pending: z.number().int().nonnegative(),
    /** `redeemed / invitesCreated`, 0…1, three decimals. `0` when nothing was created — a
     *  rate with an empty denominator is 0 arrivals out of 0 invitations, not `null`. */
    conversionRate: z.number().min(0).max(1),
    /** Expected window has passed, invite never redeemed and never cancelled: the visitor
     *  who was booked in and did not turn up. The number a front desk actually chases. */
    noShows: z.number().int().nonnegative(),
    /** Of the VISITS in range: how many arrived on an invite versus walked up. These two sum
     *  to the range's `totalVisits`. */
    preRegisteredVisits: z.number().int().nonnegative(),
    walkUpVisits: z.number().int().nonnegative(),
    /** Mean hours between raising an invite and the start of its expected window, one
     *  decimal. `null` when no invite was created in range. Short lead times mean the front
     *  desk is being told about visitors at the last minute. */
    averageLeadTimeHours: z.number().nonnegative().nullable(),
});
/** One kiosk's share of the range's check-ins, with the device's own health. */
export const deviceUsageRowDTOSchema = z.object({
    deviceId: z.string(),
    deviceName: z.string(),
    locationName: z.string(),
    status: z.enum(['PENDING', 'ACTIVE', 'REVOKED']),
    lastSeenAt: z.string().datetime().nullable(),
    visits: z.number().int().nonnegative(),
});
/**
 * `GET /api/web/analytics/devices` response — which kiosks are carrying the traffic, and
 * which are paired but idle. Professional tier (`visitor.reports_advanced`).
 *
 * BOUNDED WITHOUT PAGING, unlike the hosts report: devices are an admin-created inventory
 * (a tenant has tens, not thousands), the same argument `countVisitsByLocation` makes for
 * locations. `byDevice` includes devices with ZERO visits in range — a paired kiosk nobody
 * used is the single most useful row on this report and a visit-keyed `groupBy` would omit
 * it entirely.
 */
export const deviceUsageDTOSchema = z.object({
    /** Ranked by visits descending, ties broken by name. Includes idle devices. */
    byDevice: z.array(deviceUsageRowDTOSchema),
    /** Visits that came through a paired kiosk (`deviceId` set). */
    kioskVisits: z.number().int().nonnegative(),
    /** Visits with no device — raised through the admin console or the API. Sums with
     *  `kioskVisits` to the range's `totalVisits`. */
    deviceLessVisits: z.number().int().nonnegative(),
    devicesTotal: z.number().int().nonnegative(),
    devicesActive: z.number().int().nonnegative(),
    devicesPending: z.number().int().nonnegative(),
    devicesRevoked: z.number().int().nonnegative(),
    /** `ACTIVE` but not seen for over 24 hours — paired, believed live, and silent. The row
     *  that tells an admin a lobby tablet is unplugged before a visitor does. */
    devicesStale: z.number().int().nonnegative(),
});
/** The dwell-time bucket edges, in minutes. CODE, not configuration: two tenants whose
 *  reports use different edges cannot be compared, and comparing is what a bucket is for.
 *  The open-ended top bucket is represented by `null` and is not listed here. */
export const VISIT_DURATION_BUCKET_EDGES_MINUTES = [15, 30, 60, 120, 240];
/** How long a device may go unseen before this report calls it stale. */
export const DEVICE_STALE_AFTER_HOURS = 24;
/* -------------------------------------------------------------------------- */
/* S25 Phase 3 — the COMPLIANCE report (`visitor.reports_compliance`)          */
/* -------------------------------------------------------------------------- */
/**
 * One agreement document's acceptance coverage over the range.
 *
 * `staleVersionAcceptances` is the row auditors actually ask about: an acceptance recorded
 * against a version that is no longer current means somebody signed wording the tenant has
 * since replaced. It is not an error — an acceptance is a historical fact and is never
 * rewritten (S15) — but it IS the number that tells a compliance officer how many visitors
 * to re-paper after a policy change.
 */
export const agreementCoverageRowDTOSchema = z.object({
    documentId: z.string(),
    documentName: z.string(),
    currentVersionNumber: z.number().int().positive(),
    acceptances: z.number().int().nonnegative(),
    staleVersionAcceptances: z.number().int().nonnegative(),
    /** The most recent acceptance in range, or `null` when this document saw none. A published
     *  document nobody has signed is a real and useful row, so it is never omitted. */
    latestAcceptedAt: z.string().datetime().nullable(),
});
/**
 * `GET /api/web/analytics/compliance` response — the evidence a compliance officer is asked
 * for. Compliance tier (`visitor.reports_compliance`).
 *
 * WHAT THIS REPORT DOES NOT GATE. The evacuation roster and its export stay ungated and
 * always will (S14): a safety surface is never sold. This report LINKS to the roster; the
 * `visitsMarkedSafe` count below is a historical after-the-fact number, not the live list a
 * fire warden opens.
 *
 * COVERAGE IS NOT A PASS MARK. `agreementCoverageRate` is `visitsWithAcceptance /
 * totalVisits` over the range, and a tenant whose flows deliberately ask only contractors to
 * sign will read well below 1 with nothing wrong. The report states the ratio and its two
 * inputs and draws no conclusion — inventing a compliance verdict from a number whose
 * denominator the product does not understand would be worse than useless.
 */
export const visitorComplianceDTOSchema = z.object({
    totalVisits: z.number().int().nonnegative(),
    /** One row per agreement document the tenant has published, INCLUDING documents with zero
     *  acceptances in range. Ranked by acceptances descending, ties by name. */
    agreementDocuments: z.array(agreementCoverageRowDTOSchema),
    /** Distinct visits in range carrying at least one acceptance. */
    visitsWithAcceptance: z.number().int().nonnegative(),
    /** `visitsWithAcceptance / totalVisits`, 0…1, three decimals. `0` when the range is empty —
     *  a rate over no visits is 0 signatures out of 0 arrivals, not `null`. */
    agreementCoverageRate: z.number().min(0).max(1),
    visitsWithPhoto: z.number().int().nonnegative(),
    photoCaptureRate: z.number().min(0).max(1),
    /** Visits marked safe during an evacuation, in range. History, not the live roster. */
    visitsMarkedSafe: z.number().int().nonnegative(),
    /** The tenant's retention window. `null` means visitor PII is kept INDEFINITELY — the same
     *  meaning `VisitorRetentionSetting.retentionDays` gives it, carried through unchanged
     *  rather than defaulted here, because "not configured" and "configured to forever" are the
     *  same posture and a report that showed a number for one and a blank for the other would
     *  be lying about one of them. */
    retentionDays: z.number().int().positive().nullable(),
});
/* -------------------------------------------------------------------------- */
/* S25 Phase 4 — per-report CSV export (`visitor.log_export`)                  */
/* -------------------------------------------------------------------------- */
/**
 * Which series a Visitor Traffic export writes.
 *
 * The Traffic report renders FOUR independent tables and a CSV is ONE table. Concatenating
 * all four into a single file with a discriminator column produces something no spreadsheet
 * can pivot and no analyst asked for, so the caller names the series it wants and gets a
 * clean two-column-plus-share file per download. The values are the DTO field names the
 * report already renders, so the parameter needs no translation table.
 */
export const ANALYTICS_TRAFFIC_SERIES = ['day', 'hourOfDay', 'location', 'visitorType'];
/**
 * `GET /api/web/analytics/traffic/export` query — a range, the optional location scope, and
 * the series. Built from the raw range shape rather than `analyticsRangeQuerySchema.extend`,
 * for the same reason `analyticsPagedRangeQuerySchema` is: the range schema is a `ZodEffects`
 * once refined, and a `ZodEffects` has no `.extend`.
 */
export const analyticsTrafficExportQuerySchema = z
    .object({ ...analyticsRangeShape, series: z.enum(ANALYTICS_TRAFFIC_SERIES) })
    .superRefine(refineAnalyticsRange);
//# sourceMappingURL=analytics.js.map